import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import './Backup.css';

interface TableInfo {
  name: string;
  estimatedRows: number;
}

const Backup: React.FC = () => {
  const [tables, setTables] = useState<TableInfo[]>([]);
  const [selectedTables, setSelectedTables] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const fetchTables = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/backup/tables');
      if (response.data.success) {
        const tableList = response.data.data.tables;
        setTables(tableList);
        setSelectedTables(tableList.map((t: TableInfo) => t.name));
      }
    } catch (error) {
      console.error('Error fetching tables:', error);
      toast.error('Failed to load database tables');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTables();
  }, [fetchTables]);

  const toggleTable = (tableName: string) => {
    setSelectedTables(prev =>
      prev.includes(tableName)
        ? prev.filter(t => t !== tableName)
        : [...prev, tableName]
    );
  };

  const selectAll = () => setSelectedTables(tables.map(t => t.name));
  const selectNone = () => setSelectedTables([]);

  const handleExport = async () => {
    if (selectedTables.length === 0) {
      toast.warning('Please select at least one table');
      return;
    }

    try {
      setExporting(true);
      const response = await api.post('/backup/export',
        { tables: selectedTables },
        { responseType: 'blob', timeout: 120000 }
      );

      // Extract filename from Content-Disposition header
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'therapy_backup.xlsx';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?$/);
        if (match) filename = match[1];
      }

      // Download the file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Backup downloaded successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export backup');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="backup-loading">
        <div className="loading-spinner"></div>
        <p>Loading database info...</p>
      </div>
    );
  }

  return (
    <div className="backup-page">
      <div className="backup-header">
        <h2>Database Backup</h2>
        <p>Export your database tables to an Excel file</p>
      </div>

      <div className="backup-content">
        <div className="backup-card">
          <div className="card-header">
            <h3>Select Tables to Export</h3>
            <div className="selection-actions">
              <button onClick={selectAll} className="btn-text">Select All</button>
              <span className="divider">|</span>
              <button onClick={selectNone} className="btn-text">Select None</button>
              <span className="selected-count">
                {selectedTables.length} of {tables.length} selected
              </span>
            </div>
          </div>

          <div className="tables-grid">
            {tables.map(table => (
              <label key={table.name} className={`table-item ${selectedTables.includes(table.name) ? 'selected' : ''}`}>
                <input
                  type="checkbox"
                  checked={selectedTables.includes(table.name)}
                  onChange={() => toggleTable(table.name)}
                />
                <div className="table-info">
                  <span className="table-name">{table.name}</span>
                  <span className="table-rows">{table.estimatedRows ?? 0} rows</span>
                </div>
              </label>
            ))}
          </div>

          <div className="backup-actions">
            <button
              onClick={handleExport}
              disabled={exporting || selectedTables.length === 0}
              className="btn-export"
            >
              {exporting ? (
                <>
                  <span className="spinner-small"></span>
                  Exporting...
                </>
              ) : (
                <>Export to Excel</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Backup;
